import React, { useState } from 'react';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MTable } from '../../../../../Components';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { RichTextEditor } from '../../../../../Components/RichTextEditor/RichTextEditor';
import { OnboardingDraftSelect, hasText, lawRows, noteKey, useOnboardingDraft } from './OnboardingDraft';
import { useStyles } from './Onboarding.styles';

export const OnboardingLawNotes = () => {
  const { draft, update } = useOnboardingDraft();
  const { formatMessage: t } = useIntl();
  const classes = useStyles();
  const [formOpen, setFormOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<{ lawId: number; unitIds: number[] }>();
  const selectedLaws = draft.selectedLaws.map(law => ({ id: law.id, name: `${law.number} – ${law.name}` }));
  const savedNotesByKey = new Map<string, { id: string; lawId: number; unitIds: number[]; law: string; units: string[]; note: string }>();
  lawRows(draft).forEach(law => {
    const note = draft.notes[noteKey(law.id, law.companyUnitId)] || draft.notes[String(law.id)] || '';
    if (!hasText(note)) return;
    const key = `${law.id}:${note}`;
    const row = savedNotesByKey.get(key) || { id: key, lawId: law.id, unitIds: [], law: `${law.number} – ${law.name}`, units: [], note };
    if (!row.unitIds.includes(law.companyUnitId)) row.unitIds.push(law.companyUnitId);
    if (!row.units.includes(law.companyUnitName)) row.units.push(law.companyUnitName);
    savedNotesByKey.set(key, row);
  });
  const savedNotes = Array.from(savedNotesByKey.values()).map(row => ({ ...row, unit: row.units.join(', ') }));
  const saveNote = () => update(current => {
    const notes = { ...current.notes };
    const noteUnitIds = { ...current.noteUnitIds };
    if (editingNote) {
      editingNote.unitIds.forEach(unitId => { delete notes[noteKey(editingNote.lawId, unitId)]; });
      noteUnitIds[editingNote.lawId] = (noteUnitIds[editingNote.lawId] || []).filter(id => !editingNote.unitIds.includes(id));
    }
    (current.noteLawIds || []).forEach(lawId => {
      noteUnitIds[lawId] = Array.from(new Set([...(noteUnitIds[lawId] || []), ...(current.noteCompanyUnitIds || [])]));
      (current.noteCompanyUnitIds || []).forEach(unitId => { notes[noteKey(lawId, unitId)] = current.noteText || ''; });
    });
    return { ...current, notes, noteUnitIds, noteLawIds: [], noteCompanyUnitIds: [], noteText: '' };
  });
  const cancelEdit = () => {
    setEditingNote(undefined);
    setFormOpen(false);
    update(current => ({ ...current, noteLawIds: [], noteCompanyUnitIds: [], noteText: '' }));
  };
  const editNote = (lawId: number, unitIds: number[], note: string) => {
    setEditingNote({ lawId, unitIds });
    setFormOpen(true);
    update(current => ({ ...current, noteLawIds: [lawId], noteCompanyUnitIds: unitIds, noteText: note }));
  };

  return <>
    {!formOpen && <Box className={classes.listActions}>
      <Button color="primary" variant="outlined" onClick={() => {
        update(current => ({ ...current, noteLawIds: [], noteCompanyUnitIds: [], noteText: '' }));
        setFormOpen(true);
      }}>{t({ id: 'onboarding.add-note' })}</Button>
    </Box>}
    {formOpen && <Grid container spacing={2}>
    <Grid item xs={12} sm={6}><OnboardingDraftSelect name="note-legislation" label={t({ id: 'onboarding.select-law-notes' })} ids={draft.noteLawIds || []} options={selectedLaws} onChange={noteLawIds => update(current => ({ ...current, noteLawIds }))} /></Grid>
    <Grid item xs={12} sm={6}><OnboardingDraftSelect name="note-company-units" label={t({ id: 'form.selectCompanyUnits' })} ids={draft.noteCompanyUnitIds || []} options={draft.units} onChange={noteCompanyUnitIds => update(current => ({ ...current, noteCompanyUnitIds }))} /></Grid>
    <Grid item xs={12}><Typography variant="subtitle2">{t({ id: 'law-portal.overview-notes.form.note.label' })}</Typography><RichTextEditor value={draft.noteText || ''} onChange={noteText => update(current => ({ ...current, noteText }))} overrideConfig={{ height: 140 }} /></Grid>
    <Grid item xs={12} className={classes.formActions}>
      <Button color="primary" variant="outlined" disabled={!(draft.noteLawIds || []).length || !(draft.noteCompanyUnitIds || []).length || !hasText(draft.noteText || '')} onClick={() => { saveNote(); setEditingNote(undefined); setFormOpen(false); }}>{t({ id: editingNote ? 'form.save' : 'form.add' })}</Button>
      <Button style={{ marginLeft: 8 }} onClick={cancelEdit}>{t({ id: 'form.cancel' })}</Button>
    </Grid>
    </Grid>}
    <MTable data={savedNotes} columns={[
      { title: t({ id: 'law-portal.manage.law-list.table.law' }), field: 'law' },
      { title: t({ id: 'law-portal.overview-notes.form.company-units.label' }), field: 'unit' },
      { title: t({ id: 'law-portal.overview-notes.form.note.label' }), field: 'note', render: row => <HtmlView value={row.note} /> },
    ]} actions={[
      row => ({ icon: 'edit', tooltip: t({ id: 'component.mtable.editTooltip' }), onClick: () => editNote(row.lawId, row.unitIds, row.note) }),
      row => ({ icon: 'delete', tooltip: t({ id: 'component.mtable.deleteTooltip' }), onClick: () => {
        if (editingNote?.lawId === row.lawId && editingNote.unitIds.some(id => row.unitIds.includes(id))) cancelEdit();
        update(current => {
          const unitIds = new Set(row.unitIds);
          const noteUnitIds = { ...current.noteUnitIds, [row.lawId]: (current.noteUnitIds[row.lawId] || []).filter(id => !unitIds.has(id)) };
          const notes = { ...current.notes };
          row.unitIds.forEach(unitId => { delete notes[noteKey(row.lawId, unitId)]; });
          return { ...current, noteUnitIds, notes };
        });
      } }),
    ]} />
  </>;
};
